import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Components, FormBuilder, Formio, FormEdit } from 'lexisnexis-form';
import { Button, Navbar, Nav } from 'react-bootstrap';
import './assets/formioadmin.css';
import Api from '../../../services/Api';
import { ApiResponse } from 'apisauce';
import Validations from './Components/Validation';
import MapComponent from '../../FormIO/MappingComponent/lib/MapComponent';
import VINComponent from '../../FormIO/VinLookup/lib/VinLookupComponent';
import DriversLicenseComponent from '../../FormIO/DriversLicenseLookup/lib/DriversLicenseLookupComponent';
import LicensePlateLookupComponent from '../../FormIO/LicensePlateLookup/lib/LicensePlateLookupComponent';
import AutoCompleteComponent from '../../FormIO/AutoComplete/lib/AutoCompleteComponent';
import AreaOfDamage from '../../FormIO/AreaOfDamage/lib/AreaOfDamage';
import { CreateNewForm } from './redux-objects/actions';
import { CREATE_FORM_URL, GET_PREVIOUS_REVISION_VALIDATION_URL } from '../../shared/urls';
import fetchAuthorized from '../../../services/fetchAuthorized';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { getToken } from '../../../services/config/AdalConfig';

//Add custom components here for them to show up on the editor.
Components.addComponent('mapComponent', MapComponent);
Components.addComponent('vinLookup', VINComponent);
Components.addComponent('driversLicenseLookup', DriversLicenseComponent);
Components.addComponent('licensePlateLookup', LicensePlateLookupComponent);
Components.addComponent('AutoCompleteComponent', AutoCompleteComponent);
Components.addComponent('areasOfDamage', AreaOfDamage);

const hasValidationItems = (validation: any) => Array.isArray(validation) && validation.length > 0;

const getValidationOrBackup = (formConfig: any, backup: any) => {
  const validation = _.get(formConfig, 'properties.validation');
  return hasValidationItems(validation) ? validation : backup;
};

interface IFormEditorOwnProps {
  formURL?: string;
  formJSON?: string;
  formSave?: (formToSave: any) => Promise<any>;
  cancelEdit?: () => void;
  newForm?: Boolean;
  history: any;
}

interface IFormEditorOwnState {
  currentFormConfig: any;
  isValidating: boolean;
  validationBackup: any;
  previousValidation?: any[];
}

interface IFormEditorStateProps {
  webServiceURL: string;
}

interface IFormEditorDispathProps {
  CreateNewForm: (url: string, formSchema: any) => Promise<any>;
}

type FormEditorAllProps = IFormEditorOwnProps &
  IFormEditorDispathProps &
  IFormEditorStateProps;

class FormEditor extends Component<FormEditorAllProps, IFormEditorOwnState> {
  formBuilder = null;
  formioAuthPluginName = 'cidFormEditorAuth';

  constructor(props) {
    super(props);
    this.state = {
      currentFormConfig: { display: 'wizard' },
      isValidating: false,
      validationBackup: undefined,
      previousValidation: undefined,
    };
  }

  componentDidMount() {
    this.configureFormioAuth();

    if (this.props.formURL != undefined) {
      Api.get(this.props.formURL).then((result: ApiResponse<any>) => {
        this.setState({
          currentFormConfig: result.data,
          validationBackup: _.cloneDeep(_.get(result.data, 'properties.validation')),
        });
        this.loadPreviousValidation(_.get(result.data, 'name'));
      });
    } else if (this.props.formJSON != undefined) {
      const parsedForm = JSON.parse(this.props.formJSON.trim());
      this.setState({
        currentFormConfig: parsedForm,
        validationBackup: _.cloneDeep(_.get(parsedForm, 'properties.validation')),
      });
      this.loadPreviousValidation(_.get(parsedForm, 'name'));
    }
  }

  // Fetches the previous saved revision's validation list so the Validations view can flag which
  // rules are New or Edited in the current (latest) version. Fails silently (no baseline => no badges).
  loadPreviousValidation = (formName: string) => {
    if (!formName || !this.props.webServiceURL) {
      return;
    }

    fetchAuthorized(
      `${this.props.webServiceURL}${GET_PREVIOUS_REVISION_VALIDATION_URL}?formName=${encodeURIComponent(formName)}`
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!body || !body.hasPrevious || !body.validationJson) {
          return;
        }
        try {
          const parsed = JSON.parse(body.validationJson);
          if (Array.isArray(parsed)) {
            this.setState({ previousValidation: parsed });
          }
        } catch {
          // Ignore malformed baseline JSON; treat as no baseline.
        }
      })
      .catch(() => {
        // Ignore baseline fetch errors; badges simply won't render.
      });
  }

  componentWillUnmount() {
    if (Formio && Formio.deregisterPlugin) {
      Formio.deregisterPlugin(this.formioAuthPluginName);
    }
  }

  configureFormioAuth = () => {
    if (!Formio || !Formio.registerPlugin) {
      return;
    }

    // Ensure we do not accumulate duplicate plugins on remount/hot-reload.
    Formio.deregisterPlugin(this.formioAuthPluginName);

    Formio.registerPlugin(
      {
        priority: 100,
        preRequest: (requestArgs) => {
          const token = getToken();
          requestArgs.opts = requestArgs.opts || {};
          requestArgs.opts.headers = requestArgs.opts.headers || {};

          if (token) {
            requestArgs.opts.headers.Authorization = `Bearer ${token}`;
            Formio.setToken(token);
          }
        },
        wrapRequestPromise: (promise, requestArgs) =>
          promise.catch((err) => {
            const requestType = _.get(requestArgs, 'type');
            const requestUrl = _.get(requestArgs, 'url', '');
            const isBuilderResourcePreload =
              requestType === 'loadForms' &&
              requestUrl.includes('/form?type=resource');

            // The resource preload is optional for builder usage.
            // On unauthorized, degrade gracefully instead of crashing the dev overlay.
            if (isBuilderResourcePreload) {
              return [];
            }

            return Promise.reject(err);
          }),
      },
      this.formioAuthPluginName
    );
  };

  updateForm = () => {
    if (this.props.newForm == undefined) {
      let currentFormConfig = this.formBuilder && !this.state.isValidating
        ? _.cloneDeep(this.formBuilder.builder._form)
        : _.cloneDeep(this.state.currentFormConfig);
      const currentValidation = getValidationOrBackup(this.state.currentFormConfig, this.state.validationBackup);
      const builderValidation = _.get(currentFormConfig, 'properties.validation');
      if (hasValidationItems(currentValidation) && !hasValidationItems(builderValidation)) {
        currentFormConfig.properties = {
          ...currentFormConfig.properties,
          validation: currentValidation,
        };
      }
      currentFormConfig.properties.hideComponentsOnValid = true;
      this.props.formSave(currentFormConfig).then((result) => {
        const savedForm = JSON.parse(result);
        this.setState({
          currentFormConfig: savedForm,
          validationBackup: _.cloneDeep(_.get(savedForm, 'properties.validation')),
          previousValidation: _.cloneDeep(_.get(savedForm, 'properties.validation')) || [],
        });
      });
    } else {
      let currentFormConfig = this.formBuilder ? _.cloneDeep(this.formBuilder.state.form) : this.state.currentFormConfig;
      let machineNameParts = currentFormConfig.machineName.split(':');
      currentFormConfig.properties.hideComponentsOnValid = true; // Setting Form level flag to hide components on Validation. This is on top of the flag that manages on the componenet level.
      // Needs to be unique or form save fails
      currentFormConfig.machineName =
        machineNameParts[0] + ':' + currentFormConfig.path;
      this.props
        .CreateNewForm(
          this.props.webServiceURL + CREATE_FORM_URL,
          currentFormConfig
        )
        .then((result) => {
          this.setState({ currentFormConfig: JSON.parse(result) }, () => {
            toast.success('Successfully created the form!');
          });
        })
        .catch((error) => {
          toast.error('Failed to save the form');
        });
    }
  };

  toggleValidations = () => {
    let currentFormConfig = this.formBuilder && this.formBuilder.builder
      ? _.cloneDeep(this.formBuilder.builder._form)
      : _.cloneDeep(this.state.currentFormConfig);
    const currentValidation = getValidationOrBackup(this.state.currentFormConfig, this.state.validationBackup);
    const builderValidation = _.get(currentFormConfig, 'properties.validation');
    if (hasValidationItems(currentValidation) && !hasValidationItems(builderValidation)) {
      currentFormConfig.properties = {
        ...currentFormConfig.properties,
        validation: currentValidation,
      };
    }

    this.setState({
      isValidating: !this.state.isValidating,
      currentFormConfig,
    });
  };

  setFormValidation = (validationInfo) => {
    this.setState({
      currentFormConfig: {
        ...this.state.currentFormConfig,
        properties: {
          ...this.state.currentFormConfig.properties,
          validation: validationInfo,
        },
      },
      validationBackup: _.cloneDeep(validationInfo),
    });
  };

  cancelFormEdit = () => {
    if (this.props.cancelEdit != undefined) this.props.cancelEdit();

    this.props.history.push('/FormAdmin');
  };

  render() {
    return (
      <React.Fragment>
        <Navbar bg='dark' variant='dark'>
          <Navbar.Brand>Actions:</Navbar.Brand>
          <Button
            variant='light'
            className='btnSpaceRight'
            onClick={() => this.cancelFormEdit()}
          >
            Back to all forms
          </Button>
          <Button
            variant='secondary'
            className='btnSpaceRight'
            onClick={() => this.toggleValidations()}
          >
            {this.state.isValidating ? 'Edit Form' : 'Validations'}
          </Button>
          <Nav className='mr-auto'></Nav>
          <Button variant='primary' onClick={() => this.updateForm()}>
            Save
          </Button>
        </Navbar>
        {!this.state.isValidating && !(this.props.newForm == true) && (
          <FormBuilder
            form={this.state.currentFormConfig}
            ref={(ref) => (this.formBuilder = ref)}
          />
        )}

        {!this.state.isValidating && this.props.newForm == true && (
          <div className='divFormEditor'>
            <FormEdit
              form={this.state.currentFormConfig}
              ref={(ref) => (this.formBuilder = ref)}
              saveText=''
            />
          </div>
        )}

        {this.state.isValidating && (
          <Validations
            form={this.state.currentFormConfig}
            submission={{
              data: {
                validation: getValidationOrBackup(this.state.currentFormConfig, this.state.validationBackup) || [],
              },
            }}
            previousValidation={this.state.previousValidation}
            setValidation={this.setFormValidation}
          />
        )}
        <Navbar bg='dark' variant='dark'>
          <Navbar.Brand>Actions:</Navbar.Brand>
          <Button
            variant='light'
            className='btnSpaceRight'
            onClick={() => this.cancelFormEdit()}
          >
            Back to all forms
          </Button>
          <Button
            variant='secondary'
            className='btnSpaceRight'
            onClick={() => this.toggleValidations()}
          >
            {this.state.isValidating ? 'Edit Form' : 'Validations'}
          </Button>
          <Nav className='mr-auto'></Nav>
          <Button variant='primary' onClick={() => this.updateForm()}>
            Save
          </Button>
        </Navbar>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (reduxState: any) => {
  return {
    webServiceURL: reduxState.Application.backendURL,
  };
};

const mapDispatchToProps = {
  CreateNewForm,
};

export default connect<IFormEditorStateProps, IFormEditorDispathProps>(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(FormEditor);
